import React from 'react';
import { Box, ButtonBase, IconButton, Tooltip, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

type TitleProps = { children?: React.ReactNode; component?: unknown; href?: unknown; tabIndex?: unknown; role?: unknown };
const titleTags = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span', 'div', 'strong', 'em']);
function plainTitleProps(props: TitleProps) {
  return props.href === undefined && props.tabIndex === undefined && props.role === undefined
    && !Object.keys(props).some(key => /^on[A-Z]/.test(key));
}
function inlineTitleContent(content: React.ReactNode): boolean {
  return React.Children.toArray(content).every(child => typeof child === 'string' || typeof child === 'number'
    || (React.isValidElement<TitleProps>(child) && plainTitleProps(child.props)
      && (child.type === React.Fragment || (typeof child.type === 'string' && ['span', 'strong', 'em', 'b', 'i', 'code', 'small', 'sub', 'sup', 'br'].includes(child.type)))
      && inlineTitleContent(child.props.children)));
}

/** Count actual text rows, merging inline fragments without counting block gaps. */
function exceedsTwoLines(content: HTMLElement, root: HTMLElement): boolean {
  if (root.getBoundingClientRect().width <= 0 || getComputedStyle(root).visibility === 'hidden') return false;
  const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT);
  const rows: { top: number; bottom: number }[] = [];
  let text: Node | null;
  while ((text = walker.nextNode())) {
    if (!text.textContent?.trim()) continue;
    const range = document.createRange();
    range.selectNodeContents(text);
    if (typeof range.getClientRects !== 'function') return false;
    for (const rect of Array.from(range.getClientRects())) {
      if (rect.width <= 0 || rect.height <= 0) continue;
      const row = rows.find(candidate => Math.min(candidate.bottom, rect.bottom) - Math.max(candidate.top, rect.top) > Math.min(candidate.bottom - candidate.top, rect.height) / 2);
      if (row) { row.top = Math.min(row.top, rect.top); row.bottom = Math.max(row.bottom, rect.bottom); }
      else rows.push({ top: rect.top, bottom: rect.bottom });
    }
  }
  return rows.length > 2;
}

/** Only long primary edit content gets a control, inside its existing bubble. */
export default function ChatBubbleDisclosure({ label, preview, children, defaultExpanded = false, forceOpen = false, alwaysCollapsible = false, title, showCollapsedPreview = true }: {
  label: string; preview: string; children: React.ReactNode; defaultExpanded?: boolean; forceOpen?: boolean; alwaysCollapsible?: boolean; title?: React.ReactNode; showCollapsedPreview?: boolean;
}) {
  const [expanded, setExpanded] = React.useState(defaultExpanded || forceOpen);
  const [long, setLong] = React.useState(false);
  const root = React.useRef<HTMLDivElement>(null);
  const content = React.useRef<HTMLDivElement>(null);
  const pendingFocus = React.useRef<HTMLElement | null>(null);
  React.useEffect(() => { if (forceOpen) setExpanded(true); }, [forceOpen]);
  React.useLayoutEffect(() => {
    const element = content.current; const host = root.current;
    if (!element || !host || alwaysCollapsible) return;
    let active = true;
    const measure = () => { if (active) setLong(exceedsTwoLines(element, host)); };
    measure();
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    resize?.observe(element); resize?.observe(host);
    const mutations = new MutationObserver(measure);
    mutations.observe(element, { childList: true, characterData: true, subtree: true });
    window.addEventListener('resize', measure);
    document.fonts?.addEventListener('loadingdone', measure);
    void document.fonts?.ready.then(measure);
    return () => { active = false; resize?.disconnect(); mutations.disconnect(); window.removeEventListener('resize', measure); document.fonts?.removeEventListener('loadingdone', measure); };
  }, [children, preview, alwaysCollapsible]);
  React.useEffect(() => {
    const host = root.current;
    if (!alwaysCollapsible || !host) return;
    const form = host.closest('form');
    const target = form || host;
    const reveal = (event: Event) => {
      const submitted = event.target instanceof HTMLFormElement ? event.target : form;
      const invalid = event.type === 'invalid' && event.target instanceof HTMLElement ? event.target
        : Array.from((submitted || host).querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('input,textarea,select')).find(field => !field.disabled && !field.validity.valid);
      if (!invalid || !host.contains(invalid)) return;
      if (!expanded) { pendingFocus.current ||= invalid; setExpanded(true); }
    };
    target.addEventListener('submit', reveal, true); target.addEventListener('invalid', reveal, true);
    return () => { target.removeEventListener('submit', reveal, true); target.removeEventListener('invalid', reveal, true); };
  }, [alwaysCollapsible, expanded]);
  React.useLayoutEffect(() => { if (expanded && pendingFocus.current) { pendingFocus.current.focus(); pendingFocus.current = null; } }, [expanded]);
  const collapsible = (alwaysCollapsible || long) && !forceOpen;
  const visible = !collapsible || expanded;
  const contentId = React.useId();
  const action = `${visible ? 'Collapse' : 'Expand'} ${label}`;
  const toggle = () => setExpanded(value => !value);
  const titleButton = (text: React.ReactNode) => <ButtonBase type="button" data-chat-bubble-toggle aria-expanded={visible} aria-controls={contentId} onClick={toggle} sx={{ display: 'inline', textAlign: 'inherit', fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit', fontWeight: 'inherit', color: 'inherit', borderRadius: 0.5, '&.Mui-focusVisible': { outline: '2px solid currentColor', outlineOffset: 2 } }}>{text}</ButtonBase>;
  let displayedTitle = title;
  if (collapsible) {
    if (typeof title === 'string' || typeof title === 'number') displayedTitle = titleButton(title);
    else if (React.isValidElement<TitleProps>(title) && plainTitleProps(title.props)
      && (title.type === Typography || (typeof title.type === 'string' && titleTags.has(title.type)))
      && (title.props.component === undefined || (typeof title.props.component === 'string' && titleTags.has(title.props.component)))
      && inlineTitleContent(title.props.children)) {
      // Preserve the existing heading/typography; custom links and actions stay separate.
      displayedTitle = React.cloneElement(title, undefined, titleButton(title.props.children));
    }
  }
  return <Box ref={root} data-chat-bubble-disclosure sx={{ minWidth: 0 }}>
    {(title || collapsible) && <Box data-chat-bubble-header sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, minWidth: 0 }}>
      {title && <Box sx={{ flex: 1, minWidth: 0, ...(alwaysCollapsible ? { typography: 'body1', fontWeight: 700, letterSpacing: 'normal', textTransform: 'none', '& > :first-child': { fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit', fontWeight: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit' } } : {}) }}>{displayedTitle}</Box>}
      {collapsible && <Tooltip title={action}><IconButton type="button" data-chat-bubble-toggle size="small" color="inherit" aria-label={action} aria-expanded={visible} aria-controls={contentId} onClick={toggle} sx={{ color: 'inherit', flexShrink: 0, p: 0.25, ml: 'auto', mr: -0.5 }}>
        {visible ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </IconButton></Tooltip>}
    </Box>}
    {!visible && !alwaysCollapsible && showCollapsedPreview && <Typography data-chat-bubble-preview variant="body2" sx={{ minWidth: 0, color: 'inherit', overflowWrap: 'anywhere', display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden' }}>{preview}…</Typography>}
    <Box id={contentId} aria-hidden={!visible || undefined} sx={!visible ? { height: 0, overflow: 'hidden', visibility: 'hidden' } : undefined}>
      <Box ref={content} data-chat-bubble-content>{children}</Box>
    </Box>
  </Box>;
}
